import './Chat.css';
import api from '../api.js';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { fmtInt, fmtDateTime } from '../utils/format';

// Галочки статуса исходящего сообщения (в стиле WhatsApp).
function StatusTicks({ status }) {
    if (status === 'failed') return <span className="tick tick-failed" title="Ошибка">✕</span>;
    if (status === 'read') return <span className="tick tick-read" title="Прочитано">✓✓</span>;
    if (status === 'delivered') return <span className="tick tick-delivered" title="Доставлено">✓✓</span>;
    if (status === 'sent') return <span className="tick tick-sent" title="Отправлено">✓</span>;
    return <span className="tick tick-pending" title="Ожидает отправки">🕓</span>;
}

const STATUS_LABEL = {
    read: 'Прочитано',
    delivered: 'Доставлено',
    sent: 'Отправлено',
    failed: 'Ошибка отправки',
    enqueued: 'В очереди',
};

// Инициалы для аватара: из имени, иначе две последние цифры номера.
function initials(name, contact) {
    const n = (name || '').trim();
    if (n) {
        const parts = n.split(/\s+/).filter(Boolean).slice(0, 2);
        return parts.map((p) => p[0]).join('').toUpperCase();
    }
    const digits = (contact || '').replace(/\D/g, '');
    return digits.slice(-2) || '?';
}

// Стабильный цвет аватара по номеру — чтобы диалоги различались взглядом.
function avatarHue(contact) {
    let h = 0;
    for (const ch of String(contact || '')) h = (h * 31 + ch.charCodeAt(0)) % 360;
    return h;
}

function formatPhone(contact) {
    const d = (contact || '').replace(/\D/g, '');
    return d ? `+${d}` : contact;
}

// Время в списке диалогов: сегодня — часы, этот год — день и месяц, иначе дата.
function smartTime(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    const now = new Date();
    if (d.toDateString() === now.toDateString())
        return d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
    if (d.getFullYear() === now.getFullYear())
        return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });
    return d.toLocaleDateString('ru-RU');
}

function fullDateTime(iso) {
    return fmtDateTime(iso);
}

// Разделители дат внутри переписки.
function dayLabel(iso) {
    const d = new Date(iso);
    const now = new Date();
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    if (d.toDateString() === now.toDateString()) return 'Сегодня';
    if (d.toDateString() === yesterday.toDateString()) return 'Вчера';
    return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' });
}

function Avatar({ name, contact, size }) {
    const hue = avatarHue(contact);
    return (
        <div
            className={`chat-avatar chat-avatar-${size || 'md'}`}
            style={{ background: `hsl(${hue} 55% 88%)`, color: `hsl(${hue} 60% 28%)` }}
        >
            {initials(name, contact)}
        </div>
    );
}

function CardRow({ label, children }) {
    return (
        <div className="chat-card-row">
            <span className="chat-card-label">{label}</span>
            <span className="chat-card-value">{children}</span>
        </div>
    );
}

export default function Chat() {
    const { me } = useOutletContext() || {};
    const role = me?.userInfo?.role;
    const myPartnerName = me?.userInfo?.partnerName;

    const [partners, setPartners] = useState([]);
    const [selectedPartner, setSelectedPartner] = useState('');
    const [convs, setConvs] = useState([]);
    const [active, setActive] = useState(null);
    const [thread, setThread] = useState([]);
    const [card, setCard] = useState(null);
    const [query, setQuery] = useState('');
    const [text, setText] = useState('');
    const [error, setError] = useState('');
    const [sending, setSending] = useState(false);
    const [infoOpen, setInfoOpen] = useState(true);
    const endRef = useRef(null);

    const currentPartner = role === 'Партнер' ? myPartnerName : selectedPartner;

    // Кэш тредов: при переключении диалога переписка появляется мгновенно,
    // свежая версия подъезжает следом (ощущение Intercom — ничего не «моргает»).
    const threadCacheRef = useRef({});
    const cardCacheRef = useRef({});

    // Активный диалог нужен и в асинхронных ответах — держим его в ref,
    // чтобы поздний ответ по старому диалогу не перетёр новый.
    const activeRef = useRef(null);
    useEffect(() => { activeRef.current = active; }, [active]);

    // Отметки «просмотрено» по каждому диалогу (partner|contact -> ISO). Непрочитанный =
    // последнее сообщение входящее и пришло позже, чем мы в последний раз открывали диалог.
    const seenRef = useRef(null);
    const [, setSeenVer] = useState(0);
    if (seenRef.current === null) {
        try { seenRef.current = JSON.parse(localStorage.getItem('wave-chat-seen') || '{}'); }
        catch { seenRef.current = {}; }
    }
    const seenKey = (contact) => `${currentPartner || 'p'}|${contact}`;
    const markRead = (contact) => {
        if (!contact) return;
        const k = seenKey(contact);
        seenRef.current = { ...seenRef.current, [k]: new Date().toISOString() };
        localStorage.setItem('wave-chat-seen', JSON.stringify(seenRef.current));
        setSeenVer((v) => v + 1);
    };
    const isUnread = (c) => {
        if (c.lastDirection !== 'in') return false;
        const s = seenRef.current[seenKey(c.contact)];
        return !s || new Date(c.lastAt) > new Date(s);
    };

    useEffect(() => {
        document.title = 'Чат - WAve';
        if (role === 'Админ') {
            api.get('/api/partners/list').then((r) => setPartners(r.data || [])).catch(() => {});
        }
    }, [role]);

    const loadConvs = async () => {
        if (role === 'Админ' && !currentPartner) { setConvs([]); return; }
        try {
            const params = role === 'Админ' ? { partnerName: currentPartner } : {};
            const r = await api.get('/api/chat/conversations', { params });
            setConvs(r.data || []);
        } catch {
            setError('Ошибка загрузки диалогов');
        }
    };

    const loadThread = async (contact) => {
        try {
            const params = { contact };
            if (role === 'Админ') params.partnerName = currentPartner;
            const r = await api.get('/api/chat/thread', { params });
            const data = r.data || [];
            threadCacheRef.current[contact] = data;
            setThread((prev) => (contact === activeRef.current ? data : prev));
        } catch {
            if (contact === activeRef.current) setThread([]);
        }
    };

    const loadCard = async (contact) => {
        try {
            const params = { contact };
            if (role === 'Админ') params.partnerName = currentPartner;
            const r = await api.get('/api/chat/contact', { params });
            cardCacheRef.current[contact] = r.data;
            setCard((prev) => (contact === activeRef.current ? r.data : prev));
        } catch {
            if (contact === activeRef.current) setCard(null);
        }
    };

    useEffect(() => {
        setActive(null);
        activeRef.current = null;
        setThread([]);
        setCard(null);
        threadCacheRef.current = {};
        cardCacheRef.current = {};
        loadConvs();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPartner]);

    // Автообновление ленты, активного треда и карточки (уведомления — глобально в Root).
    useEffect(() => {
        const id = setInterval(() => {
            loadConvs();
            if (active) { loadThread(active); loadCard(active); markRead(active); }
        }, 5000);
        return () => clearInterval(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPartner, active]);

    useEffect(() => {
        endRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [thread]);

    // Публикуем открытый диалог, чтобы глобальный нотификатор не слал пуш по нему.
    useEffect(() => {
        try { localStorage.setItem('wave-chat-active', active || ''); } catch { /* игнор */ }
        return () => { try { localStorage.setItem('wave-chat-active', ''); } catch { /* игнор */ } };
    }, [active]);

    const openConv = (contact) => {
        setActive(contact);
        activeRef.current = contact;
        // Мгновенно — из кэша, затем освежаем с сервера.
        setThread(threadCacheRef.current[contact] || []);
        setCard(cardCacheRef.current[contact] || null);
        loadThread(contact);
        loadCard(contact);
        markRead(contact);
    };

    const send = async (e) => {
        e.preventDefault();
        if (!text.trim() || !active) return;
        setSending(true);
        setError('');
        try {
            await api.post('/api/chat/send', {
                partnerName: role === 'Админ' ? currentPartner : undefined,
                contact: active,
                text: text.trim(),
            });
            setText('');
            await loadThread(active);
            await loadCard(active);
            await loadConvs();
        } catch (err) {
            setError(typeof err?.response?.data === 'string' ? err.response.data : 'Ошибка отправки сообщения');
        } finally {
            setSending(false);
        }
    };

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return convs;
        return convs.filter((c) =>
            (c.name || '').toLowerCase().includes(q) ||
            (c.contact || '').includes(q.replace(/\D/g, '')) ||
            (c.lastText || '').toLowerCase().includes(q)
        );
    }, [convs, query]);

    const activeConv = convs.find((c) => c.contact === active);
    const activeName = card?.name || activeConv?.name || '';
    const unreadTotal = convs.filter(isUnread).length;

    return (
        <div className="chat-main">
            {(role === 'Админ' || unreadTotal > 0) && (
                <div className="chat-header-row">
                    {role === 'Админ' && (
                        <select value={selectedPartner} onChange={(e) => setSelectedPartner(e.target.value)}>
                            <option value="">Выберите партнёра</option>
                            {partners.map((p) => (
                                <option key={p.id} value={p.name}>{p.name}</option>
                            ))}
                        </select>
                    )}
                    {unreadTotal > 0 && (
                        <span className="chat-unread-note">
                            Непрочитанных диалогов: <b>{fmtInt(unreadTotal)}</b>
                        </span>
                    )}
                </div>
            )}

            {error && <p className="error">{error}</p>}

            <div className={`chat-body ${active && infoOpen ? 'with-info' : ''}`}>
                <div className="chat-list-pane">
                    <div className="chat-search">
                        <input
                            placeholder="Поиск по имени, номеру, тексту…"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                        />
                    </div>
                    <div className="chat-list">
                        {filtered.length === 0 ? (
                            <div className="chat-empty">
                                {role === 'Админ' && !currentPartner
                                    ? 'Выберите партнёра'
                                    : query ? 'Ничего не найдено' : 'Диалогов пока нет'}
                            </div>
                        ) : (
                            filtered.map((c) => (
                                <div
                                    key={c.contact}
                                    className={`chat-conv ${active === c.contact ? 'active' : ''} ${isUnread(c) ? 'unread' : ''}`}
                                    onClick={() => openConv(c.contact)}
                                >
                                    <Avatar name={c.name} contact={c.contact} size="md" />
                                    <div className="chat-conv-body">
                                        <div className="chat-conv-top">
                                            <b>{c.name || formatPhone(c.contact)}</b>
                                            <span className="chat-conv-time">{smartTime(c.lastAt)}</span>
                                        </div>
                                        <div className="chat-conv-last">
                                            {isUnread(c) && <span className="chat-conv-dot" />}
                                            {c.lastDirection === 'out' ? 'Вы: ' : ''}{c.lastText}
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>

                <div className="chat-thread-pane">
                    {!active ? (
                        <div className="chat-empty">Выберите диалог слева</div>
                    ) : (
                        <>
                            <div className="chat-thread-header">
                                <Avatar name={activeName} contact={active} size="sm" />
                                <div className="chat-thread-who">
                                    <b>{activeName || formatPhone(active)}</b>
                                    {activeName && <span>{formatPhone(active)}</span>}
                                </div>
                                <button
                                    type="button"
                                    className={`chat-info-toggle ${infoOpen ? 'on' : ''}`}
                                    onClick={() => setInfoOpen((v) => !v)}
                                    title={infoOpen ? 'Скрыть карточку' : 'Показать карточку'}
                                >
                                    ℹ
                                </button>
                            </div>
                            <div className="chat-thread">
                                {thread.map((m, i) => {
                                    const prev = thread[i - 1];
                                    const newDay = !prev
                                        || new Date(prev.timestamp).toDateString() !== new Date(m.timestamp).toDateString();
                                    return (
                                        <div key={m.id} className="chat-row-wrap">
                                            {newDay && <div className="chat-day">{dayLabel(m.timestamp)}</div>}
                                            <div className={`chat-bubble ${m.direction === 'out' ? 'out' : 'in'}`}>
                                                <div className="chat-bubble-text">{m.text}</div>
                                                <div className="chat-bubble-meta">
                                                    <span>{new Date(m.timestamp).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}</span>
                                                    {m.direction === 'out' && <StatusTicks status={m.status} />}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                                <div ref={endRef} />
                            </div>
                            <form className="chat-composer" onSubmit={send}>
                                <input
                                    placeholder="Введите сообщение…"
                                    value={text}
                                    onChange={(e) => setText(e.target.value)}
                                />
                                <button type="submit" disabled={sending || !text.trim()}>
                                    {sending ? '…' : 'Отправить'}
                                </button>
                            </form>
                        </>
                    )}
                </div>

                {active && infoOpen && (
                    <div className="chat-info-pane">
                        <div className="chat-card-hero">
                            <Avatar name={activeName} contact={active} size="lg" />
                            <b>{activeName || 'Без имени'}</b>
                            <span>{formatPhone(active)}</span>
                        </div>

                        <div className="chat-card-section">
                            <div className="chat-card-title">Диалог</div>
                            <CardRow label="Первое сообщение">{fullDateTime(card?.firstAt)}</CardRow>
                            <CardRow label="Последнее сообщение">{fullDateTime(card?.lastAt)}</CardRow>
                            <CardRow label="Последний ответ клиента">{fullDateTime(card?.lastInAt)}</CardRow>
                            <CardRow label="Всего сообщений">{fmtInt(card?.total)}</CardRow>
                            <CardRow label="Входящих / исходящих">
                                {card ? `${fmtInt(card.inCount)} / ${fmtInt(card.outCount)}` : '—'}
                            </CardRow>
                        </div>

                        <div className="chat-card-section">
                            <div className="chat-card-title">Последнее исходящее</div>
                            <CardRow label="Статус">
                                {card?.lastOutStatus ? (
                                    <span className={`chat-status-pill ${card.lastOutStatus}`}>
                                        <StatusTicks status={card.lastOutStatus} />
                                        {STATUS_LABEL[card.lastOutStatus] || card.lastOutStatus}
                                    </span>
                                ) : '—'}
                            </CardRow>
                            <CardRow label="Отправлено">{fullDateTime(card?.lastOutAt)}</CardRow>
                        </div>

                        <div className="chat-card-section">
                            <div className="chat-card-title">Канал</div>
                            <CardRow label="Партнёр">{card?.partnerName || currentPartner || '—'}</CardRow>
                            <CardRow label="Наш номер">
                                {card?.sourceNumber ? formatPhone(card.sourceNumber) : '—'}
                            </CardRow>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
